// Sign-in with Microsoft Entra ID (MSAL, auth code + PKCE, redirect flow) and access
// tokens for the ACC API. Test builds read a pre-signed token from sessionStorage instead.
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { InteractionRequiredAuthError, PublicClientApplication, type AccountInfo } from '@azure/msal-browser';
import { config } from './config';

export interface SignedInUser {
  name: string;
  username: string;
}

interface AuthState {
  ready: boolean;
  user: SignedInUser | null;
  signIn: () => Promise<void>;
  signOut: () => Promise<void>;
  getToken: () => Promise<string>;
}

const AuthContext = createContext<AuthState | null>(null);

export const TEST_TOKEN_KEY = 'acc-test-token';
export const TEST_USER_KEY = 'acc-test-user';

function msalAuth(): Omit<AuthState, 'ready' | 'user'> & { init: () => Promise<SignedInUser | null> } {
  const pca = new PublicClientApplication({
    auth: {
      clientId: config.clientId,
      authority: `https://login.microsoftonline.com/${config.tenantId}`,
      redirectUri: `${location.origin}/`,
      postLogoutRedirectUri: `${location.origin}/`,
      navigateToLoginRequestUrl: true,
    },
    cache: { cacheLocation: 'sessionStorage' },
  });
  const scopes = [config.apiScope];
  const toUser = (a: AccountInfo | null) => (a ? { name: a.name ?? a.username, username: a.username } : null);
  return {
    async init() {
      await pca.initialize();
      const result = await pca.handleRedirectPromise();
      const account = result?.account ?? pca.getActiveAccount() ?? pca.getAllAccounts()[0] ?? null;
      if (account) pca.setActiveAccount(account);
      return toUser(account);
    },
    async signIn() {
      await pca.loginRedirect({ scopes });
    },
    async signOut() {
      await pca.logoutRedirect({ account: pca.getActiveAccount() ?? undefined });
    },
    async getToken() {
      const account = pca.getActiveAccount();
      if (!account) { await pca.loginRedirect({ scopes }); throw new Error('Redirecting to sign in'); }
      try {
        return (await pca.acquireTokenSilent({ scopes, account })).accessToken;
      } catch (err) {
        if (err instanceof InteractionRequiredAuthError) { await pca.acquireTokenRedirect({ scopes, account }); }
        throw err;
      }
    },
  };
}

function testAuth(): Omit<AuthState, 'ready' | 'user'> & { init: () => Promise<SignedInUser | null> } {
  return {
    async init() {
      const raw = sessionStorage.getItem(TEST_USER_KEY);
      return raw ? (JSON.parse(raw) as SignedInUser) : null;
    },
    async signIn() { /* tests sign in by seeding sessionStorage */ },
    async signOut() { sessionStorage.removeItem(TEST_TOKEN_KEY); sessionStorage.removeItem(TEST_USER_KEY); location.assign('/'); },
    async getToken() {
      const t = sessionStorage.getItem(TEST_TOKEN_KEY);
      if (!t) throw new Error('No test token');
      return t;
    },
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const impl = useMemo(() => (config.authMode === 'test' ? testAuth() : msalAuth()), []);
  const [state, setState] = useState<{ ready: boolean; user: SignedInUser | null }>({ ready: false, user: null });
  useEffect(() => {
    impl.init().then((user) => setState({ ready: true, user }), () => setState({ ready: true, user: null }));
  }, [impl]);
  const value = useMemo<AuthState>(() => ({ ...state, signIn: impl.signIn, signOut: impl.signOut, getToken: impl.getToken }), [state, impl]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth outside AuthProvider');
  return ctx;
}
